/**
 * Who decides access to the access-management route (Zugangsverwaltung), what
 * that route is allowed to wait for, and therefore what it shows.
 *
 * Pure and framework-free (house pattern, cf. bootTenantResolution.ts) so the
 * rule is pinned by tests instead of only being exercised through App.tsx.
 *
 * The same question — "does the operational account endpoint answer for this
 * caller at all?" — is answered for the /account route by
 * `hooks/useCanAccessAccount.ts`. That hook is also where the correct handling
 * of the answer already lived: it gates its own `isResolving` on `!skip`, which
 * is exactly what this route failed to do. Change one, look at the other.
 */

/**
 * Whether the `accessManagement` ENTITLEMENT is what answers the question for
 * this tenant.
 *
 * Self-service tenants, whoever is looking at them. A legacy tenant has no row
 * in the operational DB at all, so `tenant.entitlements` is meaningless for it
 * (see `common/tenantStore.ts`); it is answered by the locker permission from
 * the Auth0 JWT instead — already loaded by the time the routes render, since
 * App gates them on `permissionsLoaded`.
 *
 * A MASTER used to be excluded here, because `GET /admin/account` answers for
 * the master's own tenant and would have given the wrong tenant's answer. That
 * was a limitation of the read, never a decision: a master viewing a venue
 * tenant with no device saw its access-management entry — the exact thing
 * ADR-0001 Amendment 5 removes — because the gate fell back to the master's own
 * `read:lockers`. `useEntitlementState` now resolves through
 * `useViewedTenantAccount`, which reads the VIEWED tenant by slug for a master,
 * so the entitlement is that tenant's and the exclusion is gone.
 */
export const entitlementDecidesAccessManagement = (
    isSelfService: boolean,
): boolean => isSelfService;

/**
 * Whether the user may open the device page (rather than the upsell page).
 * ADR-0001 Amendment 4: not entitled is not a refusal, it opens the page
 * describing the add-on.
 */
export const resolveAccessManagementAccess = (
    hasLockerPermission: boolean,
    entitledToAccessManagement: boolean,
    isSelfService: boolean,
): boolean =>
    (entitlementDecidesAccessManagement(isSelfService)
        ? entitledToAccessManagement
        : hasLockerPermission);

/**
 * Whether the route must WAIT for `getAccount` before it can render anything.
 *
 * It waits only when the entitlement is what decides — the same predicate the
 * decision itself uses, so what the gate waits for and what it acts on cannot
 * disagree.
 *
 * They did disagree, and that is the bug this module was extracted for: the
 * route blocked on the entitlement for EVERY tenant, including the legacy ones
 * whose answer it then discarded in favour of the locker permission. So a
 * legacy tenant's locker page hung on a new-backend call whose result it never
 * reads — and every other screen kept working, because the rest of a legacy
 * tenant's admin UI is served by the old backend.
 */
export const shouldWaitForAccessManagementEntitlement = (
    entitlementResolving: boolean,
    isSelfService: boolean,
): boolean =>
    entitlementResolving && entitlementDecidesAccessManagement(isSelfService);

/** What the access-management route shows. */
export type AccessManagementView = 'loading' | 'devices' | 'upsell' | 'unavailable';

/**
 * What a FAILED entitlement lookup resolves to.
 *
 * For the tenant's own staff: `unavailable`. Guessing is worse than saying so —
 * `devices` shows lockers to a tenant that may not hold the add-on, `upsell`
 * sells it to one that already pays (ADR-0001 Amendment 4 forbids that
 * outright).
 *
 * For a MASTER: their own `read:lockers`, which is what decided for them before
 * the entitlement did. Making the viewed tenant's entitlement the answer must
 * not cost internal staff access to a customer's devices during an outage of
 * the very call that answers it — a tenant-scoped rule, not a reduction in what
 * kolula staff can reach. The entitlement still decides whenever it answers,
 * which is the case that matters here: innodom's read succeeds and says no.
 */
const viewAfterFailedLookup = (
    hasLockerPermission: boolean,
    isMaster: boolean,
): AccessManagementView => {
    if (!isMaster) {
        return 'unavailable';
    }
    return hasLockerPermission ? 'devices' : 'upsell';
};

/**
 * The route's whole decision, from raw facts to one of three views.
 *
 * It takes the FACTS rather than two pre-computed booleans on purpose. The
 * defect was never in either predicate — both were correct — it was at the call
 * site, which handed the route a "still resolving" flag where it needed "still
 * resolving AND the resolution matters". Deriving both here leaves the caller
 * nothing to mix up, and puts the branch that broke production under test.
 *
 * `loading` is a loader, never an empty render: a route that answers a pending
 * request with nothing is not a lighter loading state but an unreportable one.
 */
export const accessManagementView = (
    entitlementResolving: boolean,
    hasLockerPermission: boolean,
    entitledToAccessManagement: boolean,
    isSelfService: boolean,
    entitlementFailed: boolean = false,
    /**
     * Used for ONE branch: which way a failed lookup falls (see
     * `viewAfterFailedLookup`). It is deliberately not an input to the decision
     * itself — when the entitlement answers, it answers for the tenant and the
     * viewer's role is irrelevant.
     */
    isMaster: boolean = false,
): AccessManagementView => {
    // A failed lookup outranks a pending one. `loading` is the only view with
    // no way out, and QA #2439 defect B is what that costs: on `fun` the route
    // showed a spinner in an otherwise empty page, with no error and no empty
    // state, so there was nothing to report and nothing to retry. Where the
    // entitlement decides, a lookup that did not answer is its own view —
    // "we could not check" is information; a spinner that never stops is not.
    //
    // Only where the entitlement decides: a legacy tenant is answered from the
    // locker permission and never needed that call, so a failure there must not
    // take its page away.
    if (entitlementDecidesAccessManagement(isSelfService) && entitlementFailed) {
        return viewAfterFailedLookup(hasLockerPermission, isMaster);
    }
    if (shouldWaitForAccessManagementEntitlement(entitlementResolving, isSelfService)) {
        return 'loading';
    }
    return resolveAccessManagementAccess(
        hasLockerPermission, entitledToAccessManagement, isSelfService,
    ) ? 'devices' : 'upsell';
};

/**
 * Whether the SIDEBAR carries the access-management entry at all.
 *
 * ADR-0001 Amendment 5 (PO/CEO, 2026-09-22): a tenant that operates no smart
 * lock or comparable IoT device has nothing under access management, so it does
 * not get the entry. Amendment 4 had made the entry unconditional and let the
 * entitlement decide only what opens — an upsell page for everyone else. In
 * practice that puts a permanently contentless menu item in front of every
 * room-rental tenant (innodom manages venue bookings and hands over keys by
 * hand), and it was reported to us as a defect rather than read as an offer.
 *
 * It is expressed as a function OF THE ROUTE'S OWN DECISION rather than as a
 * second predicate over the same facts, so the nav item and the page cannot
 * drift apart — the entry is there exactly when the route has something to
 * show:
 *
 * - `devices`  → the tenant has access management: entry.
 * - `upsell`   → nothing behind it (this is the case Amendment 5 changes): none.
 * - `loading`  → not known yet; an entry that appears on resolution is better
 *                than one that vanishes.
 * - `unavailable` → the lookup FAILED. Keep the entry: hiding it here would
 *                make a failure look like an absence (`.claude/rules`), and it
 *                would take the device page away from a tenant that does hold
 *                the add-on whenever `GET /admin/account` hiccups. The page
 *                itself says what went wrong, which is how it gets reported.
 *
 * Legacy tenants are unaffected: `accessManagementView` answers them from the
 * locker permission, never from the entitlement, so a station operator's entry
 * is still gated exactly as before. Masters are NOT an exception any more —
 * they see the viewed tenant's answer, which is the point: a tenant without a
 * device has nothing under access management for anyone, internal staff
 * included. The single exception is a lookup that FAILS, where a master falls
 * back to `read:lockers` rather than losing access to a customer's devices
 * during an outage (`viewAfterFailedLookup`).
 */
export const showAccessManagementEntry = (view: AccessManagementView): boolean =>
    view === 'devices' || view === 'unavailable';
